import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Minus, Package, Plus, ShoppingBag } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { ProductCard } from '../components/ProductCard';
import { ProductDetailSections } from '../components/ProductDetailSections';
import {
  getProductBadgeLabel,
  getProductImageObjectPosition,
  getProductAttributeLabel,
  getProductById,
  getRelatedProducts,
  normalizeBodycareDetails,
  normalizeFoodDetails,
  normalizeHouseholdDetails,
  normalizeProductCategory,
  normalizeProductSubcategory,
} from '../data/products';
import { useProducts } from '../hooks/useProducts';
import { useProductPageSettings } from '../hooks/useProductPageSettings';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/useToast';

export default function Product() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { products } = useProducts();
  const productPageSettings = useProductPageSettings();

  const product = id ? getProductById(products, id) : null;
  const relatedProducts = id ? getRelatedProducts(products, id) : [];
  const productAttributeLabel = product ? getProductAttributeLabel(product) : '';

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImageIndex(0);
  }, [id]);

  if (!product) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga text-center">
            <h1 className="font-display text-2xl font-bold mb-4">Termék nem található</h1>
            <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Vissza a termékekhez
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast({
      title: 'Kosárba téve',
      description: `${quantity} db ${product.name} hozzáadva a kosárhoz.`,
    });
  };
  const normalizedCategory = normalizeProductCategory(product.category);
  const isFoodProduct = normalizedCategory === 'foods';
  const normalizedSubcategory = normalizeProductSubcategory(normalizedCategory, product.subcategory);
  const productBadgeLabel = getProductBadgeLabel(product);
  const productImages = Array.from(
    new Set([product.image, ...(product.galleryImages || [])].filter(Boolean)),
  );
  const activeImage = productImages[activeImageIndex] || product.image;
  const foodDetails = normalizeFoodDetails(product.foodDetails);
  const householdDetails = normalizeHouseholdDetails(product.householdDetails);
  const bodycareDetails = normalizeBodycareDetails(product.bodycareDetails);
  const primaryMeta = normalizedCategory === 'foods'
    ? [
      { label: 'Kiszerelés', value: foodDetails.netQuantity },
      { label: 'Egységár', value: foodDetails.unitPrice },
    ]
    : normalizedCategory === 'household'
      ? [
        { label: 'Kiszerelés', value: householdDetails.packSize },
        { label: 'Illat', value: householdDetails.scent },
      ]
      : [
        { label: 'Kiszerelés', value: bodycareDetails.netQuantity },
        { label: 'Állag / illat', value: bodycareDetails.texture },
      ];
  const heroTags = normalizedCategory === 'foods'
    ? foodDetails.dietaryTags.slice(0, 4)
    : normalizedCategory === 'household'
      ? householdDetails.ecoFeatures.slice(0, 4)
      : bodycareDetails.certifications.slice(0, 4);
  const flavorNotes = product?.flavorNotes || [];
  const showFlavorNotes = normalizedSubcategory === 'loose-tea' && flavorNotes.length > 0;
  const backgroundPosition = `${productPageSettings.backgroundFocusX}% ${productPageSettings.backgroundFocusY}%`;

  return (
    <Layout>
      <div className={`section-padding relative isolate overflow-hidden ${isFoodProduct ? 'bg-background text-foreground' : ''}`}>
        {productPageSettings.backgroundImage ? (
          <>
            <div
              className="absolute inset-0 -z-20 bg-cover bg-fixed bg-no-repeat"
              style={{
                backgroundImage: `url(${productPageSettings.backgroundImage})`,
                backgroundPosition,
              }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 -z-10 bg-gradient-to-b from-background/70 via-background/40 to-background/90"
              aria-hidden="true"
            />
          </>
        ) : null}
        <div className="container-zinga relative z-10">
          <button
            onClick={() => navigate(-1)}
            className={`mb-8 inline-flex items-center gap-2 transition-colors ${
              isFoodProduct
                ? 'text-muted-foreground hover:text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Vissza
          </button>

          <div className="grid items-start gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:gap-16">
            <div className="mx-auto w-4/5 lg:sticky lg:top-28">
              <div className="relative aspect-square overflow-hidden rounded-[2rem] border border-primary/20 bg-secondary/45 shadow-soft">
                {product.isHungarian ? (
                  <span
                    className="absolute right-4 top-4 z-10 inline-flex h-7 w-11 flex-col overflow-hidden rounded border border-white/90 bg-white shadow-sm"
                    aria-label="Hazai termék"
                    title="Hazai termék"
                  >
                    <span className="h-1/3 w-full bg-[#ce2939]" />
                    <span className="h-1/3 w-full bg-white" />
                    <span className="h-1/3 w-full bg-[#477050]" />
                  </span>
                ) : null}
                {activeImage ? (
                  <img
                    src={activeImage}
                    alt={`${product.name}${activeImageIndex > 0 ? ` – ${activeImageIndex + 1}. kép` : ''}`}
                    className="h-full w-full object-cover transition-opacity duration-300"
                    style={{
                      objectPosition: activeImageIndex === 0
                        ? getProductImageObjectPosition(product)
                        : '50% 50%',
                    }}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    <Package className="h-12 w-12" strokeWidth={1.4} />
                  </div>
                )}
              </div>
              {productImages.length > 1 ? (
                <div className="mt-4 grid grid-cols-3 gap-3" aria-label="Termékképek">
                  {productImages.map((imageUrl, index) => (
                    <button
                      key={imageUrl}
                      type="button"
                      onClick={() => setActiveImageIndex(index)}
                      className={`aspect-square overflow-hidden rounded-xl border-2 bg-secondary/40 transition-all ${
                        index === activeImageIndex
                          ? 'border-primary shadow-sm'
                          : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                      aria-label={`${index + 1}. termékkép megtekintése`}
                      aria-pressed={index === activeImageIndex}
                    >
                      <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="flex flex-col pt-1">
              <div className="mb-5 flex flex-wrap gap-2">
                {productBadgeLabel ? (
                  <span className={`badge-category ${isFoodProduct ? 'border border-primary/15 bg-primary/[0.06] text-primary' : 'bg-primary/[0.07] text-primary'}`}>
                    {productBadgeLabel}
                  </span>
                ) : null}
                {product.isBestseller ? (
                  <span className={`badge-category ${isFoodProduct ? 'bg-[#D7B55B] text-[#0B2F22]' : 'bg-secondary text-secondary-foreground'}`}>Népszerű</span>
                ) : null}
                {productAttributeLabel ? (
                  <span className={`badge-category ${isFoodProduct ? 'border border-[#B38C30]/25 bg-[#D7B55B]/20 text-[#6F5520]' : 'bg-tea text-foreground'}`}>{productAttributeLabel}</span>
                ) : null}
              </div>

              <h1 className="font-display text-[1.5rem] font-bold leading-tight md:text-[2.4rem]">{product.name}</h1>
              {product.shortDescription ? (
                <p className={`mt-5 max-w-xl text-base leading-7 md:text-lg ${isFoodProduct ? 'text-foreground/90' : 'text-foreground/68'}`}>
                  {product.shortDescription}
                </p>
              ) : null}

              <div className={`mt-7 flex flex-wrap items-end gap-x-5 gap-y-2 border-b pb-7 ${isFoodProduct ? 'border-primary/10' : 'border-border/70'}`}>
                <p className="font-display text-2xl font-semibold">
                  {product.price.toLocaleString('hu-HU')} Ft
                </p>
                {primaryMeta.filter(({ value }) => value).map(({ label, value }) => (
                  <span key={label} className={`pb-0.5 text-sm ${isFoodProduct ? 'text-foreground/80' : 'text-muted-foreground'}`}>
                    {label}: {value}
                  </span>
                ))}
              </div>

              {heroTags.length > 0 ? (
                <div className="mt-6 flex flex-wrap gap-x-5 gap-y-3">
                  {heroTags.map((tag) => (
                    <span key={tag} className={`inline-flex items-center gap-2 text-sm ${isFoodProduct ? 'text-foreground/90' : 'text-foreground/72'}`}>
                      <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${isFoodProduct ? 'bg-[#D7B55B] text-[#0B2F22]' : 'bg-primary/10 text-primary'}`}>
                        <Check className="h-3 w-3" strokeWidth={2.6} />
                      </span>
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}

              {product.longDescription ? (
                <div className={`mt-7 border-l-2 pl-5 ${isFoodProduct ? 'border-[#D7B55B]/55' : 'border-primary/25'}`}>
                  <h2 className="font-display text-lg font-semibold">A termékről</h2>
                  <p className={`mt-2 whitespace-pre-line text-[15px] leading-7 ${isFoodProduct ? 'text-foreground/90' : 'text-foreground/75'}`}>
                    {product.longDescription}
                  </p>
                </div>
              ) : null}

              {showFlavorNotes ? (
                <div className={`mt-7 rounded-2xl p-5 ${isFoodProduct ? 'border border-primary/10 bg-secondary/60' : 'bg-secondary/65'}`}>
                  <h3 className="mb-3 font-display font-semibold">Ízjegyek</h3>
                  <div className="flex flex-wrap gap-2">
                    {flavorNotes.map((note) => (
                      <span key={note} className={`rounded-full border px-3 py-1.5 text-xs ${isFoodProduct ? 'border-primary/10 bg-card text-primary' : 'border-border/70 bg-card'}`}>
                        {note}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="mt-8 rounded-3xl border border-border/70 bg-card p-5 text-foreground shadow-sm sm:p-6">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-end">
                  <div>
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      Mennyiség
                    </span>
                    <div className="flex h-12 items-center overflow-hidden rounded-xl border border-border bg-background">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="flex h-full w-11 items-center justify-center transition-colors hover:bg-secondary"
                        aria-label="Mennyiség csökkentése"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-10 text-center font-medium" aria-live="polite">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="flex h-full w-11 items-center justify-center transition-colors hover:bg-secondary"
                        aria-label="Mennyiség növelése"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={handleAddToCart}
                    className="btn-primary min-h-12 flex-1"
                  >
                    <ShoppingBag className="h-5 w-5" />
                    Kosárba — {(product.price * quantity).toLocaleString('hu-HU')} Ft
                  </button>
                </div>
              </div>
            </div>
          </div>

          <ProductDetailSections
            product={product}
            category={normalizedCategory}
            onAddToCart={handleAddToCart}
          />

          {relatedProducts.length > 0 && (
            <div className="mt-24">
              <h2 className="font-display text-2xl font-bold mb-8">Ez is tetszhet</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map((related) => (
                  <ProductCard key={related.id} product={related} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
